<!--Lector de códigos QR-->
<template>
  <v-overlay :value="visible">
    <v-container>
      <div
        :class="{ 'fullscreen': fullscreen }"
        ref="wrapper"
        @fullscreenchange="onFullscreenChange"
      >
        <qrcode-stream :camera="camera" @decode="onDecode" @init="onInit">
          <button @click="fullscreen = !fullscreen" class="fullscreen-button">
            <v-icon v-if="!fullscreen" color="primary">fullscreen</v-icon>
            <v-icon v-if="fullscreen" color="primary">fullscreen_exit</v-icon>
          </button>
          <!-- MENSAJE EN CASO DE REGISTRO EXITOSO -->
          <div v-if="validationSuccess" class="qr-validation validation-success">
            <h2 class="headline font-weight-bold">¡Bienvenido!</h2>
          </div>

          <!-- MENSAJE EN CASO DE ERROR -->
          <div v-if="validationFailure" class="qr-validation validation-failure">
            <h2 class="headline font-weight-bold">{{errorCode}}</h2>
          </div>

          <!-- LOADER DE VALIDACIÓN -->
          <div v-if="validationPending" class="qr-validation validation-pending">
            <v-row justify="center" align="center">
              <v-col cols="12">
                <v-progress-circular indeterminate size="80" :width="7" color="accent"></v-progress-circular>
              </v-col>
              <v-col cols="12">
                <h2 class="headline font-weight-bold">Procesando...</h2>
              </v-col>
            </v-row>
          </div>
        </qrcode-stream>
      </div>
      <v-row justify="center" class="mt-5">
        <v-btn large @click="$emit('hide-qr')">Volver</v-btn>
      </v-row>
    </v-container>
  </v-overlay>
</template>
<script>
import { QrcodeStream } from "vue-qrcode-reader";
import { mapActions } from "vuex";

export default {
  components: {
    QrcodeStream
  },
  props: {
    visible: { type: Boolean, required: true }
  },
  data() {
    return {
      isValid: undefined,
      camera: "auto",
      result: null,
      fullscreen: false,
      errorCode: ""
    };
  },
  watch: {
    /**
     * Función para verificar cambios en la visualización de la cámara
     * @param { boolean } enterFullscreen Bandera que indica si se solicitó la vista 
     * de pantalla completa.
     */
    fullscreen(enterFullscreen) {
      if (enterFullscreen) {
        this.requestFullscreen();
      } else {
        this.exitFullscreen();
      }
    }
  },
  computed: {
    /**
     * Función para indicar el sistema que se están procesando los datos del QR
     */
    validationPending() {
      return this.isValid === undefined && this.camera === "off";
    },

    /**
     * Función para indicar al sistema un procesamiento correcto de los datos.
     */
    validationSuccess() {
      return this.isValid === true;
    },

    /**
     * Función para indicar al sistema que hubo un error al procesar los datos
     */
    validationFailure() {
      return this.isValid === false;
    }
  },

  methods: {
    ...mapActions("order", ["checkIn"]),

    /**
     * Función en caso de que el usuario salga del modo de pantalla completa sin usar el botón 
     * de salida. Por ejemplo ESC. en computadoras o un botón "<-" en un dispositivo.
     * @param { object } event Enviado por el componente de QR.
     */
    onFullscreenChange(event) {
      this.fullscreen = document.fullscreenElement !== null;
    },

    /**
     * Función que solicita un cambio a patalla completa en cualquier navegador.
     */
    requestFullscreen() {
      const elem = this.$refs.wrapper;

      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        /* Firefox */
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        /* IE/Edge */
        elem.msRequestFullscreen();
      }
    },

    /**
     * Función que solicita salir del modo de pantalla completa en cualquier navegador.
     */
    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        /* Firefox */
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        /* Chrome, Safari and Opera */
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        /* IE/Edge */
        document.msExitFullscreen();
      }
    },

    /**
     * Función al iniciar el componente del lector del código de QR.
     */
    onInit(promise) {
      promise
        .then(() => {
          //console.log("Successfully initilized! Ready for scanning now!");
        })
        .catch(error => {
          if (error.name === "NotAllowedError") {
            this.errorMessage = "Hey! I need access to your camera";
          } else if (error.name === "NotFoundError") {
            this.errorMessage = "Do you even have a camera on your device?";
          } else if (error.name === "NotSupportedError") {
            this.errorMessage =
              "Seems like this page is served in non-secure context (HTTPS, localhost or file://)";
          } else if (error.name === "NotReadableError") {
            this.errorMessage =
              "Couldn't access your camera. Is it already in use?";
          } else if (error.name === "OverconstrainedError") {
            this.errorMessage =
              "Constraints don't match any installed camera. Did you asked for the front camera although there is none?";
          } else {
            this.errorMessage = "UNKNOWN ERROR: " + error.message;
          }
        });
    },

    /**
     * Función para regresar la validación al estado inicial.
     */
    resetValidationState() {
      this.isValid = undefined;
    },

    /**
     * Función que valida la lectura del código QR.
     * @param { object } content Resultado del lector QR.
     */
    async onDecode(content) {
      try {
        this.result = content;
        this.turnCameraOff();
        let result = await this.checkIn(JSON.parse(content));
        if (result == "Ok") {
          this.isValid = true;
          await this.timeout(1000);
        } else {
          this.isValid = false;
          this.errorCode = result;
          await this.timeout(2000);
        }
        this.resetValidationState();
        this.turnCameraOn();
      } catch (error) {
        this.errorCode = "No se encontró al asistente, vuelva a intentarlo.";
        this.isValid = false;
        await this.timeout(2000);
        this.resetValidationState();
        this.turnCameraOn();
      }
    },

    /**
     * Función para iniciar la cámara del dispositivo.
     */
    turnCameraOn() {
      this.camera = "auto";
    },

    /**
     * Función para apagar la camara del dispositivo.
     */
    turnCameraOff() {
      this.camera = "off";
    },

    timeout(ms) {
      return new Promise(resolve => {
        window.setTimeout(resolve, ms);
      });
    }
  }
};
</script>

<style scoped>
.qr-validation {
  position: absolute;
  width: 100%;
  height: 100%;
  text-align: center;
  font-weight: bold;
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: black;
}

.validation-pending {
  background-color: rgba(255, 255, 255, 0.8);
}
.validation-failure {
  background-color: rgba(255, 194, 194, 0.8);
  width: 100%;
  height: 100%;
}
.validation-success {
  background-color: rgba(196, 255, 173, 0.8);
}
.fullscreen {
  position: fixed;
  z-index: 1000;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
}

.fullscreen-button {
  background-color: white;
  position: absolute;
  bottom: 0;
  right: 0;
  margin: 1rem;
}
.fullscreen-button:focus {
  outline: none;
}
</style>